import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DropdownModule } from '@epicuro-next/ataraxia/dropdown';
import { IconsModule } from '@epicuro-next/ataraxia/icons';
import { ModalModule } from '@epicuro-next/ataraxia/modal';
import { TooltipDirective } from '@epicuro-next/ataraxia/tooltip';
import { AccountsService } from '@epicuro-next/data/accounts';
import { selectVMIEnabledBrandsIds } from '@epicuro-next/state/accounts-state';
import { selectAccountId } from '@epicuro-next/state/session-state';
import { selectSites } from '@epicuro-next/state/site-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { downloadFile } from '@epicuro-next/utilities-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  Observable,
  of,
} from 'rxjs';
import {
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

type BrandItem = { id: string; displayName: string };
type ViewModel = {
  selected: BrandItem | null;
  brands: BrandItem[];
  downloading: boolean;
};
@Component({
  selector: 'epc-vmi-ddr-download-modal',
  templateUrl: './vmi-ddr-download-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownModule,
    CommonModule,
    ModalModule,
    IconsModule,
    TooltipDirective,
  ],
})
export class VmiDdrDownloadModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly accountService = inject(AccountsService);
  private readonly modal = inject(NgbActiveModal);
  private readonly store = inject(Store);

  private readonly cmiBrands$ = this.store.pipe(
    select(selectVMIEnabledBrandsIds),
  );
  private readonly sites$ = this.store.pipe(select(selectSites));
  private readonly downloading$ = new BehaviorSubject(false);

  public readonly selectedBrand$ = new BehaviorSubject<BrandItem | null>(null);
  public readonly brands$: Observable<BrandItem[]> = combineLatest([
    this.cmiBrands$,
    this.sites$,
  ]).pipe(
    map(([brandIds, sites]) => {
      return brandIds.map((id) => ({
        id,
        displayName: sites.find(({ brandId }) => brandId === id)?.title ?? id,
      }));
    }),
  );

  public readonly vm$: Observable<ViewModel> = combineLatest([
    this.downloading$,
    this.selectedBrand$,
    this.brands$,
  ]).pipe(
    map(([downloading, selected, brands]) => ({
      downloading,
      selected,
      brands,
    })),
  );

  public ngOnInit() {
    this.brands$
      .pipe(
        take(1),
        filter((brands) => brands?.length === 1),
        tap(([brand]) => this.selectedBrand$.next(brand)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public close() {
    this.modal.close();
  }

  public confirm() {
    this.store
      .pipe(
        select(selectAccountId),
        take(1),
        withLatestFrom(this.selectedBrand$),
        tap(() => {
          this.downloading$.next(true);
        }),
        filter(([accountId, brand]) => !!accountId && !!brand),
        switchMap(([accountId, brand]: [string, BrandItem]) => {
          return this.accountService
            .downloadVmiDDrReport(accountId, brand.id)
            .pipe(
              tap((responseBlob) => {
                this.downloading$.next(false);

                const fileName = `ddr_report_${accountId}_${brand.id}`;
                downloadFile(responseBlob.body as Blob, fileName);
                this.store.dispatch(
                  showSnackBarMessage({
                    message: $localize`:VMI|VMI/DDR download success message@@vmiDdrDownloadSuccess:Report is downloaded successfully`,
                    messageType: MessageType.SUCCESS,
                  }),
                );
                this.close();
              }),
            );
        }),
        catchError((error) => {
          this.store.dispatch(
            showSnackBarMessage({
              message: $localize`:VMI|VMI/DDR download failure message@@vmiDdrDownloadFailure:Failed to download report`,
              messageType: MessageType.DANGER,
            }),
          );
          this.downloading$.next(false);
          return of(error);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public selectBrand(brand: BrandItem) {
    this.selectedBrand$.next(brand);
  }
}
